import { useState, useCallback, useMemo, useEffect } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { DrawerSessionPair } from './useDrawerAskAI'

interface UseFloatingAskAISessionArgs {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
  isActive: boolean
}

interface UseFloatingAskAISessionReturn {
  handleSubmit: (message: string) => Promise<void>
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  hasActiveSession: boolean
}

export function useFloatingAskAISession({
  submitMessage,
  messages,
  isStreaming,
  isActive,
}: UseFloatingAskAISessionArgs): UseFloatingAskAISessionReturn {
  // messages.length is intentionally excluded from deps to avoid resetting mid-session.
  const [sessionStartIndex, setSessionStartIndex] = useState(() => messages.length)

  useEffect(() => {
    if (isActive) {
      setSessionStartIndex(messages.length)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  const handleSubmit = useCallback(async(message: string) => {
    await submitMessage(message)
  }, [submitMessage])

  const { latestResponse, sessionHistory, isResponseStreaming, isWaitingForResponse } = useMemo(() => {
    const sessionMessages = messages.slice(sessionStartIndex)
    const pairs: Array<DrawerSessionPair> = []

    for (let i = 0; i < sessionMessages.length; i++) {
      const msg = sessionMessages[i]
      if (msg.role !== 'user') continue

      const nextMsg = sessionMessages[i + 1]
      const answer = nextMsg?.role === 'assistant' ? nextMsg : undefined
      pairs.push({ question: msg, answer })
    }

    const latestPair = pairs.length > 0 ? pairs[pairs.length - 1] : undefined
    const latestResponse = latestPair?.answer
    const lastMessage = messages[messages.length - 1]
    const isResponseStreaming = !!(isStreaming && lastMessage?.id === latestResponse?.id)
    const isWaitingForResponse = !!(
      isStreaming
      && latestPair?.answer === undefined
      && lastMessage?.id === latestPair?.question.id
    )

    return { latestResponse, sessionHistory: pairs, isResponseStreaming, isWaitingForResponse }
  }, [messages, sessionStartIndex, isStreaming])

  return {
    handleSubmit,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession: sessionHistory.length > 0,
  }
}
